<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">商品屬性定義</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="openCreateForm">
          <i class="ri-add-line"></i>
          新增屬性
        </button>
      </div>
    </div>

    <div class="page-container">
    <p class="page-desc">設定商品的屬性類型（例如：尺寸、容量、顏色），建立後可在商品規格中選擇。</p>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else class="attr-list">
      <div v-if="!attributeDefinitions.length" class="empty-state-card">
        <i class="ri-stack-line empty-icon"></i>
        <p>尚無屬性定義</p>
        <button class="btn btn-primary" @click="openCreateForm">新增第一個屬性</button>
      </div>

      <template v-else>
        <!-- 排序提示列 -->
        <div class="sort-toolbar">
          <span class="sort-hint"><i class="ri-drag-move-2-line"></i> 拖曳左側圖示可調整顯示順序</span>
          <button
            v-if="orderDirty"
            class="btn btn-primary btn-sm"
            :disabled="savingOrder"
            @click="saveOrder"
          >
            <span v-if="savingOrder" class="spinner spinner-sm"></span>
            <i v-else class="ri-save-line"></i>
            {{ savingOrder ? '儲存中...' : '儲存排序' }}
          </button>
        </div>

        <div
          v-for="(def, idx) in attributeDefinitions"
          :key="def.id"
          class="attr-card"
          :class="{ 'drag-over': dragOverIdx === idx, 'dragging': dragIdx === idx }"
          draggable="true"
          @dragstart="onDragStart(idx, $event)"
          @dragover.prevent="onDragOver(idx)"
          @dragleave="onDragLeave"
          @drop.prevent="onDrop(idx)"
          @dragend="onDragEnd"
        >
          <div class="attr-card-header">
            <div class="drag-handle" title="拖曳排序">
              <i class="ri-drag-move-2-line"></i>
            </div>
            <div class="attr-card-title">
              <span class="attr-order-num">{{ idx + 1 }}</span>
              <span class="attr-name">{{ def.attributeName }}</span>
              <span class="attr-code">{{ def.attributeCode }}</span>
              <span class="attr-type-badge">{{ valueTypeLabel(def.valueType) }}</span>
            </div>
            <div class="attr-card-actions">
              <button class="btn btn-sm btn-icon-only" :disabled="idx === 0" @click="moveUp(idx)" title="上移">
                <i class="ri-arrow-up-s-line"></i>
              </button>
              <button class="btn btn-sm btn-icon-only" :disabled="idx === attributeDefinitions.length - 1" @click="moveDown(idx)" title="下移">
                <i class="ri-arrow-down-s-line"></i>
              </button>
              <button class="btn btn-sm btn-secondary" @click="openEditForm(def)" title="編輯">
                <i class="ri-edit-line"></i> 編輯
              </button>
              <button class="btn btn-sm btn-danger" @click="confirmDelete(def)" title="刪除">
                <i class="ri-delete-bin-line"></i>
              </button>
            </div>
          </div>
          <div class="attr-card-options" v-if="(def.options || []).length">
            <span class="options-label">選項：</span>
            <span v-for="opt in def.options" :key="opt.id" class="option-tag">{{ opt.value }}</span>
          </div>
          <div class="attr-card-options" v-else>
            <span class="options-label text-muted">無預設選項（自由輸入）</span>
          </div>
        </div>
      </template>
    </div>

    <!-- 新增/編輯表單 Modal -->
    <div v-if="showForm" class="modal-overlay" @click.self="closeForm">
      <div class="modal-box">
        <h3 class="modal-title">{{ editingId ? '編輯屬性定義' : '新增屬性定義' }}</h3>

        <div class="form-row">
          <label class="form-label required">屬性代碼</label>
          <input
            v-model="form.attributeCode"
            type="text"
            class="form-control"
            placeholder="英文小寫，例：size、capacity、color"
            maxlength="50"
            :disabled="!!editingId"
          />
          <p class="field-hint">建立後無法修改，建議使用英文小寫（如 size、color）</p>
        </div>

        <div class="form-row">
          <label class="form-label required">顯示名稱</label>
          <input
            v-model="form.attributeName"
            type="text"
            class="form-control"
            placeholder="例：尺寸、容量、顏色"
            maxlength="100"
          />
        </div>

        <div class="form-row">
          <label class="form-label">選項值</label>
          <div class="options-editor">
            <div
              v-for="(opt, idx) in form.options"
              :key="idx"
              class="option-row"
              :class="{ 'opt-drag-over': optDragOverIdx === idx, 'opt-dragging': optDragIdx === idx }"
              draggable="true"
              @dragstart="onOptDragStart(idx, $event)"
              @dragover.prevent="onOptDragOver(idx)"
              @dragleave="onOptDragLeave"
              @drop.prevent="onOptDrop(idx)"
              @dragend="onOptDragEnd"
            >
              <span class="opt-drag-handle" title="拖曳排序">
                <i class="ri-drag-move-2-line"></i>
              </span>
              <span class="opt-order-num">{{ idx + 1 }}</span>
              <input
                v-model="form.options[idx].value"
                type="text"
                class="form-control"
                :placeholder="`選項 ${idx + 1}`"
              />
              <button type="button" class="btn btn-sm btn-icon-only" :disabled="idx === 0" @click="moveOptionUp(idx)" title="上移">
                <i class="ri-arrow-up-s-line"></i>
              </button>
              <button type="button" class="btn btn-sm btn-icon-only" :disabled="idx === form.options.length - 1" @click="moveOptionDown(idx)" title="下移">
                <i class="ri-arrow-down-s-line"></i>
              </button>
              <button type="button" class="btn btn-sm btn-danger btn-icon" @click="removeOption(idx)" title="刪除">
                <i class="ri-close-line"></i>
              </button>
            </div>
            <button type="button" class="btn btn-secondary btn-sm add-option-btn" @click="addOption">
              <i class="ri-add-line"></i> 新增選項
            </button>
          </div>
          <p class="field-hint">若有選項，規格填寫時以下拉選單呈現；無選項則為自由文字輸入。拖曳左側圖示可調整順序。</p>
        </div>

        <div class="modal-actions">
          <button class="btn btn-secondary" @click="closeForm">取消</button>
          <button class="btn btn-primary" @click="submitForm" :disabled="submitting">
            {{ submitting ? '儲存中...' : (editingId ? '儲存' : '新增') }}
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { productService } from '@/api/modules/products.js';
import { showSuccess, showError, showConfirm } from '@/utils/swal.js';

export default {
  name: 'ClientProductAttributesIndex',
  setup() {
    const loading = ref(false);
    const attributeDefinitions = ref([]);
    const showForm = ref(false);
    const editingId = ref(null);
    const submitting = ref(false);

    // 屬性卡片排序
    const orderDirty = ref(false);
    const savingOrder = ref(false);
    const dragIdx = ref(null);
    const dragOverIdx = ref(null);

    // 選項值排序
    const optDragIdx = ref(null);
    const optDragOverIdx = ref(null);

    const form = reactive({
      attributeCode: '',
      attributeName: '',
      options: [],
    });

    const loadDefinitions = async () => {
      loading.value = true;
      try {
        const res = await productService.getAttributeDefinitions();
        attributeDefinitions.value = (res?.success && res?.data?.items) ? res.data.items : [];
        orderDirty.value = false;
      } catch (err) {
        await showError('載入失敗', err.response?.data?.message || err.message);
      } finally {
        loading.value = false;
      }
    };

    // 拖曳排序
    const onDragStart = (idx, e) => {
      dragIdx.value = idx;
      e.dataTransfer.effectAllowed = 'move';
    };
    const onDragOver = (idx) => {
      if (idx !== dragIdx.value) dragOverIdx.value = idx;
    };
    const onDragLeave = () => {
      dragOverIdx.value = null;
    };
    const onDrop = (toIdx) => {
      const fromIdx = dragIdx.value;
      if (fromIdx === null || fromIdx === toIdx) return;
      const items = [...attributeDefinitions.value];
      const [moved] = items.splice(fromIdx, 1);
      items.splice(toIdx, 0, moved);
      attributeDefinitions.value = items;
      orderDirty.value = true;
      dragIdx.value = null;
      dragOverIdx.value = null;
    };
    const onDragEnd = () => {
      dragIdx.value = null;
      dragOverIdx.value = null;
    };

    // 上下移動
    const moveUp = (idx) => {
      if (idx === 0) return;
      const items = [...attributeDefinitions.value];
      [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
      attributeDefinitions.value = items;
      orderDirty.value = true;
    };
    const moveDown = (idx) => {
      if (idx === attributeDefinitions.value.length - 1) return;
      const items = [...attributeDefinitions.value];
      [items[idx], items[idx + 1]] = [items[idx + 1], items[idx]];
      attributeDefinitions.value = items;
      orderDirty.value = true;
    };

    // 儲存排序
    const saveOrder = async () => {
      savingOrder.value = true;
      try {
        await Promise.all(
          attributeDefinitions.value.map((def, idx) => {
            const opts = (def.options || []).map((o, i) => ({ value: o.value, sortOrder: i }));
            return productService.updateAttributeDefinition(def.id, {
              attributeName: def.attributeName,
              valueType: opts.length ? 'select' : 'text',
              sortOrder: idx,
              options: opts,
            });
          })
        );
        await showSuccess('排序已儲存');
        orderDirty.value = false;
      } catch (err) {
        await showError('儲存失敗', err.response?.data?.message || err.message);
      } finally {
        savingOrder.value = false;
      }
    };

    const valueTypeLabel = (type) => {
      return { text: '文字', select: '選單', number: '數字' }[type] || type;
    };

    const openCreateForm = () => {
      editingId.value = null;
      form.attributeCode = '';
      form.attributeName = '';
      form.options = [];
      showForm.value = true;
    };

    const openEditForm = (def) => {
      editingId.value = def.id;
      form.attributeCode = def.attributeCode;
      form.attributeName = def.attributeName;
      form.options = (def.options || []).map(o => ({ value: o.value, sortOrder: o.sortOrder ?? 0 }));
      showForm.value = true;
    };

    const closeForm = () => {
      showForm.value = false;
    };

    const addOption = () => {
      form.options.push({ value: '', sortOrder: form.options.length });
    };

    const removeOption = (idx) => {
      form.options.splice(idx, 1);
    };

    // 選項拖曳排序
    const onOptDragStart = (idx, e) => {
      optDragIdx.value = idx;
      e.dataTransfer.effectAllowed = 'move';
    };
    const onOptDragOver = (idx) => {
      if (idx !== optDragIdx.value) optDragOverIdx.value = idx;
    };
    const onOptDragLeave = () => {
      optDragOverIdx.value = null;
    };
    const onOptDrop = (toIdx) => {
      const fromIdx = optDragIdx.value;
      if (fromIdx === null || fromIdx === toIdx) return;
      const items = [...form.options];
      const [moved] = items.splice(fromIdx, 1);
      items.splice(toIdx, 0, moved);
      form.options = items;
      optDragIdx.value = null;
      optDragOverIdx.value = null;
    };
    const onOptDragEnd = () => {
      optDragIdx.value = null;
      optDragOverIdx.value = null;
    };

    const moveOptionUp = (idx) => {
      if (idx === 0) return;
      const items = [...form.options];
      [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
      form.options = items;
    };
    const moveOptionDown = (idx) => {
      if (idx === form.options.length - 1) return;
      const items = [...form.options];
      [items[idx], items[idx + 1]] = [items[idx + 1], items[idx]];
      form.options = items;
    };

    const submitForm = async () => {
      const code = (form.attributeCode || '').trim();
      const name = (form.attributeName || '').trim();
      if (!code || !name) {
        await showError('請填寫屬性代碼與顯示名稱');
        return;
      }
      const options = form.options
        .map((o, i) => ({ value: (o.value || '').trim(), sortOrder: i }))
        .filter(o => o.value);

      submitting.value = true;
      try {
        if (editingId.value) {
          await productService.updateAttributeDefinition(editingId.value, {
            attributeName: name,
            valueType: options.length ? 'select' : 'text',
            options,
          });
          await showSuccess('屬性已更新');
        } else {
          await productService.createAttributeDefinition({
            attributeCode: code,
            attributeName: name,
            valueType: options.length ? 'select' : 'text',
            options: options.length ? options : undefined,
          });
          await showSuccess('屬性已新增');
        }
        closeForm();
        await loadDefinitions();
      } catch (err) {
        await showError('操作失敗', err.response?.data?.message || err.message);
      } finally {
        submitting.value = false;
      }
    };

    const confirmDelete = async (def) => {
      const confirmed = await showConfirm('確認刪除', `確定要刪除屬性「${def.attributeName}」嗎？刪除後現有規格的該屬性值不受影響，但新規格將無法選擇此屬性。`);
      if (!confirmed) return;
      try {
        await productService.deleteAttributeDefinition(def.id);
        await showSuccess('已刪除');
        await loadDefinitions();
      } catch (err) {
        await showError('刪除失敗', err.response?.data?.message || err.message);
      }
    };

    onMounted(loadDefinitions);

    return {
      loading,
      attributeDefinitions,
      showForm,
      editingId,
      submitting,
      form,
      orderDirty,
      savingOrder,
      dragIdx,
      dragOverIdx,
      valueTypeLabel,
      openCreateForm,
      openEditForm,
      closeForm,
      addOption,
      removeOption,
      submitForm,
      confirmDelete,
      onDragStart,
      onDragOver,
      onDragLeave,
      onDrop,
      onDragEnd,
      moveUp,
      moveDown,
      saveOrder,
      optDragIdx,
      optDragOverIdx,
      onOptDragStart,
      onOptDragOver,
      onOptDragLeave,
      onOptDrop,
      onOptDragEnd,
      moveOptionUp,
      moveOptionDown,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-desc {
  color: #666;
  font-size: 14px;
  margin: -8px 0 20px 0;
}

.attr-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-state-card {
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #888;
}

.empty-icon {
  font-size: 40px;
  display: block;
  margin-bottom: 12px;
  color: #ccc;
}

.empty-state-card p {
  margin-bottom: 16px;
}

.attr-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px 20px;
}

/* 排序工具列 */
.sort-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f5f8ff;
  border: 1px solid #c8d8f8;
  border-radius: 8px;
  font-size: 13px;
}

.sort-hint {
  color: #4a6fa5;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 拖曳把手 */
.drag-handle {
  padding: 4px 8px;
  cursor: grab;
  color: #bbb;
  font-size: 18px;
  flex-shrink: 0;
  border-radius: 4px;
  transition: color 0.15s;
}

.drag-handle:hover {
  color: #4a90d9;
  background: #f0f4ff;
}

.attr-card[draggable="true"] {
  cursor: default;
}

.attr-card.dragging {
  opacity: 0.4;
}

.attr-card.drag-over {
  border-color: #4a90d9;
  background: #f0f6ff;
  box-shadow: 0 0 0 2px #4a90d933;
}

/* 序號 */
.attr-order-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: #e8f0fb;
  color: #3a6bc9;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

/* 上下移動按鈕 */
.btn-icon-only {
  padding: 4px 8px;
  color: #666;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  transition: background 0.15s, color 0.15s;
}

.btn-icon-only:hover:not(:disabled) {
  background: #f0f4ff;
  color: #4a90d9;
  border-color: #4a90d9;
}

.btn-icon-only:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.attr-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.attr-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.attr-name {
  font-size: 16px;
  font-weight: 600;
}

.attr-code {
  font-size: 13px;
  color: #888;
  background: #f4f4f4;
  padding: 2px 8px;
  border-radius: 4px;
}

.attr-type-badge {
  font-size: 12px;
  color: #4a90d9;
  background: #e8f0fb;
  padding: 2px 8px;
  border-radius: 4px;
}

.attr-card-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.attr-card-options {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 13px;
}

.options-label {
  color: #888;
}

.option-tag {
  background: #f0f0f0;
  border-radius: 4px;
  padding: 2px 10px;
  font-size: 13px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: #fff;
  border-radius: 10px;
  padding: 28px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
}

.form-row {
  margin-bottom: 18px;
}

.form-label {
  display: block;
  font-weight: 500;
  margin-bottom: 6px;
}

.form-label.required::after {
  content: ' *';
  color: #c00;
}

.field-hint {
  font-size: 12px;
  color: #888;
  margin: 4px 0 0 0;
}

.options-editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-row {
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 4px 6px;
  border-radius: 6px;
  transition: background 0.1s, border-color 0.1s;
  border: 1px solid transparent;
}

.option-row .form-control {
  flex: 1;
}

.option-row.opt-drag-over {
  border-color: #4a90d9;
  background: #eef4ff;
}

.option-row.opt-dragging {
  opacity: 0.35;
}

.opt-drag-handle {
  cursor: grab;
  color: #bbb;
  font-size: 16px;
  flex-shrink: 0;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color 0.15s;
}

.opt-drag-handle:hover {
  color: #4a90d9;
}

.opt-order-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: #f0f0f0;
  color: #888;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.btn-icon {
  padding: 6px 10px;
  flex-shrink: 0;
}

.add-option-btn {
  align-self: flex-start;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.text-muted {
  color: #aaa;
}
</style>
